import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { kybData } from "../../api/config/constants";
import { useSelector } from 'react-redux';

type GeoDetails = {
  city: string;
  regionName: string;
  country: string;
  isp: string;
  org: string;
  timezone: string;
  lat: number;
  lon: number;
};

type ModuleApiResponse = {
  result?: any;
  status?: string;
  statusCode?: number | string;
  metadata?: any;
  metaData?: any;
};

type ModuleResult = {
  module?: string;
  attempts: string;
  moduleId?: string;
  apiResponse: ModuleApiResponse;
  processedAt: string;
  previousAttempts?: any[];
  flag?: { code: string; message: string };
};

type KYBItem = {
  kyb_id: string;
  client_id: string;
  application_status: string;
  s3_selfie_image_url?: string | null;
  s3_pan_image_url?: string | null;
  s3_aadhar_image_url?: string | null;
  metadata: {
    results: ModuleResult[];
    userDetails?: Record<string, any>;
    workflowDetails?: Record<string, any>;
    applicationStatus?: string;
  };
  created_at?: string;
  updated_at?: string;
};

type KYBDummyResponse = {
  success: boolean;
  message: string;
  result: KYBItem[];
};

let dummyData: any = kybData;

if (typeof window !== 'undefined' && (window as any).__KYB_DUMMY__) {
  dummyData = (window as any).__KYB_DUMMY__;
}

function mapStatusToChipColor(status?: string): 'default' | 'success' | 'warning' | 'info' | 'error' | 'primary' | 'secondary' {
  const value = (status || '').toLowerCase();
  if (['auto_approved', 'active', 'success', 'pass'].includes(value)) return 'success';
  if (['warning', 'partial', 'partially_available'].includes(value)) return 'warning';
  if (['pending', 'processing'].includes(value)) return 'info';
  if (['failed', 'error'].includes(value)) return 'error';
  return 'default';
}

function safeGet<T>(fn: () => T, fallback: T): T {
  try {
    const v = fn();
    return v === undefined || v === null ? fallback : v;
  } catch (_) {
    return fallback;
  }
}

export default function KYBUserData(): JSX.Element {
  const kyb: KYBItem | null = safeGet(() => dummyData.result[0], null);
  const data = useSelector((state: any) => state.userKyb);

  if (!kyb) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h6" color="text.secondary" align="center">
          No KYB data available.
        </Typography>
      </Container>
    );
  }

  const geoIPData: ModuleResult | undefined = kyb.metadata.results.find((r) => r.module === 'GeoIP');
  const gstinData: ModuleResult | undefined = kyb.metadata.results.find((r) => r.module === 'GSTIN API');
  const cinData: ModuleResult | undefined = kyb.metadata.results.find((r) => r.module === 'CIN API');

  return (
    <Box maxWidth="xl" sx={{ width:'100%' }}>
      <Stack >
        <Box sx={{ mb: 2 }}>
          <Typography 
            variant="h3" 
            fontWeight={700} 
            gutterBottom
            sx={{ mb: 2 }}
          >
            KYB Details
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ fontWeight: 400 }}
          >
            Know Your Business verification information
          </Typography>
        </Box>

        {/* Basic Information Card */}
        <Card 
          variant="outlined" 
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <CardHeader 
            title="Basic Information" 
            sx={{ 
              pb: 1,
              '& .MuiCardHeader-title': {
                fontSize: '1.25rem',
                fontWeight: 600
              }
            }}
          />
          <CardContent sx={{ pt: 0 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6} lg={4}>
                <Typography 
                  variant="subtitle2" 
                  color="text.secondary" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 1,
                    fontSize: '1rem'
                  }}
                >
                  KYB ID
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'Roboto Mono, monospace',
                    fontSize: '1.1rem',
                    lineHeight: 1.5
                  }}
                >
                  {kyb.kyb_id}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <Typography 
                  variant="subtitle2" 
                  color="text.secondary" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 1,
                    fontSize: '1rem'
                  }}
                >
                  Client ID
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'Roboto Mono, monospace',
                    fontSize: '1.1rem',
                    lineHeight: 1.5
                  }}
                >
                  {kyb.client_id}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <Typography 
                  variant="subtitle2" 
                  color="text.secondary" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 1,
                    fontSize: '1rem'
                  }}
                >
                  Application Status
                </Typography>
                <Box mt={1}>
                  <Chip
                    size="medium"
                    color={mapStatusToChipColor(kyb.application_status)}
                    label={(kyb.application_status || '').replace('_', ' ').toUpperCase()}
                    sx={{ fontSize: '0.9rem', px: 2, py: 0.5 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Geographic Information Card */}
        {geoIPData && (
          <Card 
            variant="outlined" 
            sx={{ 
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <CardHeader 
              title="Geographic Information" 
              sx={{ 
                pb: 1,
                '& .MuiCardHeader-title': {
                  fontSize: '1.25rem',
                  fontWeight: 600
                }
              }}
            />
            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6} lg={4}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1rem'
                    }}
                  >
                    IP Address
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'Roboto Mono, monospace',
                      fontSize: '1.1rem',
                      lineHeight: 1.5
                    }}
                  >
                    {safeGet(() => geoIPData.apiResponse.result.ipAddress, '-')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} lg={6}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1rem'
                    }}
                  >
                    Location
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.1rem',
                      lineHeight: 1.5
                    }}
                  >
                    {(() => {
                      const geo: GeoDetails = safeGet(() => geoIPData.apiResponse.result.geoDetails, {} as GeoDetails);
                      return geo.city ? `${geo.city}, ${geo.regionName}, ${geo.country}` : '-';
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1rem'
                    }}
                  >
                    ISP
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.1rem',
                      lineHeight: 1.5
                    }}
                  >
                    {safeGet(() => geoIPData.apiResponse.result.geoDetails.isp, '-')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1rem'
                    }}
                  >
                    Organization
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.1rem',
                      lineHeight: 1.5
                    }}
                  >
                    {safeGet(() => geoIPData.apiResponse.result.geoDetails.org, '-')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1rem'
                    }}
                  >
                    Timezone
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.1rem',
                      lineHeight: 1.5
                    }}
                  >
                    {safeGet(() => geoIPData.apiResponse.result.geoDetails.timezone, '-')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1rem'
                    }}
                  >
                    Coordinates
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'Roboto Mono, monospace',
                      fontSize: '1.1rem',
                      lineHeight: 1.5
                    }}
                  >
                    {(() => {
                      const geo: GeoDetails = safeGet(() => geoIPData.apiResponse.result.geoDetails, {} as GeoDetails);
                      return geo.lat !== undefined && geo.lon !== undefined ? `${geo.lat}, ${geo.lon}` : '-';
                    })()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* GSTIN Information Card */}
        {gstinData && (
          <Card 
            variant="outlined" 
            sx={{ 
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <CardHeader 
              title="GSTIN Information" 
              sx={{ 
                pb: 1,
                '& .MuiCardHeader-title': {
                  fontSize: '1.25rem',
                  fontWeight: 600
                }
              }}
            />
            <CardContent sx={{ pt: 0 }}>
              {/* Basic Details Section */}
              <Box mb={4}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 3,
                    color: 'primary.main',
                    fontSize: '1.25rem'
                  }}
                >
                  Basic Details
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6} lg={4}>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        fontSize: '1rem'
                      }}
                    >
                      GSTIN
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontFamily: 'Roboto Mono, monospace',
                        fontSize: '1.1rem',
                        lineHeight: 1.5
                      }}
                    >
                      {safeGet(() => gstinData.apiResponse.result.basicDetails.gstin, '-')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        fontSize: '1rem'
                      }}
                    >
                      Legal Business Name
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontSize: '1.1rem',
                        lineHeight: 1.5
                      }}
                    >
                      {safeGet(() => gstinData.apiResponse.result.basicDetails.legalBusinessName, '-')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        fontSize: '1rem'
                      }}
                    >
                      Trade Name
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontSize: '1.1rem',
                        lineHeight: 1.5
                      }}
                    >
                      {safeGet(() => gstinData.apiResponse.result.basicDetails.tradeName, '-')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        fontSize: '1rem'
                      }}
                    >
                      Registration Status
                    </Typography>
                    <Box mt={1}>
                      <Chip
                        size="medium"
                        color={mapStatusToChipColor(safeGet(() => gstinData.apiResponse.result.basicDetails.registrationStatus, ''))}
                        label={safeGet(() => gstinData.apiResponse.result.basicDetails.registrationStatus, '-')}
                        sx={{ fontSize: '0.9rem', px: 2, py: 0.5 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        fontSize: '1rem'
                      }}
                    >
                      Registration Date
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontSize: '1.1rem',
                        lineHeight: 1.5
                      }}
                    >
                      {safeGet(() => gstinData.apiResponse.result.basicDetails.registrationDate, '-')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        fontSize: '1rem'
                      }}
                    >
                      Constitution of Business
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontSize: '1.1rem',
                        lineHeight: 1.5
                      }}
                    >
                      {safeGet(() => gstinData.apiResponse.result.basicDetails.constitutionOfBusiness, '-')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        fontSize: '1rem'
                      }}
                    >
                      Aggregate Turnover
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontSize: '1.1rem',
                        lineHeight: 1.5
                      }}
                    >
                      {safeGet(() => gstinData.apiResponse.result.basicDetails.aggreTurnOver, '-')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <Typography 
                      variant="subtitle2" 
                      color="text.secondary" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        fontSize: '1rem'
                      }}
                    >
                      Aadhaar Verified
                    </Typography>
                    <Box mt={1}>
                      <Chip
                        size="medium"
                        color={safeGet(() => gstinData.apiResponse.result.basicDetails.aadhaarVerified, '') === 'Yes' ? 'success' : 'error'}
                        label={safeGet(() => gstinData.apiResponse.result.basicDetails.aadhaarVerified, '-')}
                        sx={{ fontSize: '0.9rem', px: 2, py: 0.5 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Members Section */}
              <Box mb={4}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    color: 'primary.main',
                    fontSize: '1.25rem'
                  }}
                >
                  Members
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {safeGet(() => gstinData.apiResponse.result.basicDetails.memberDetails, [] as string[]).map((member: string, idx: number) => (
                    <Chip 
                      key={idx} 
                      label={(member || '').trim()} 
                      variant="outlined" 
                      color="primary" 
                      size="medium"
                      sx={{ fontSize: '0.9rem', px: 2, py: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Business Nature Section */}
              <Box mb={4}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    color: 'primary.main',
                    fontSize: '1.25rem'
                  }}
                >
                  Business Nature
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {safeGet(() => gstinData.apiResponse.result.basicDetails.businessNature, [] as string[]).map((nature: string, idx: number) => (
                    <Chip 
                      key={idx} 
                      label={nature} 
                      variant="outlined" 
                      color="secondary" 
                      size="medium"
                      sx={{ fontSize: '0.9rem', px: 2, py: 0.5 }}
                    />
                  ))}
                </Box>
              </Box>

              <Divider sx={{ my: 4 }} />

              {/* Principal Address Section */}
              <Box mb={4}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    color: 'primary.main',
                    fontSize: '1.25rem'
                  }}
                >
                  Principal Address
                </Typography>
                <Box 
                  sx={{ 
                    bgcolor: 'grey.50', 
                    borderRadius: 2, 
                    p: 3,
                    border: '1px solid',
                    borderColor: 'grey.200'
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      lineHeight: 1.6,
                      fontSize: '1.1rem'
                    }}
                  >
                    {safeGet(() => gstinData.apiResponse.result.branchDetails.principalAddress.address, '-')}
                  </Typography>
                </Box>
              </Box>

              {/* HSN Details Section */}
              {safeGet(() => gstinData.apiResponse.result.hsnDetails, null) && (
                <>
                  <Divider sx={{ my: 4 }} />
                  <Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 3,
                        color: 'primary.main',
                        fontSize: '1.25rem'
                      }}
                    >
                      HSN Details
                    </Typography>
                    <Grid container spacing={4}>
                      <Grid item xs={12} lg={6}>
                        <Typography 
                          variant="subtitle1" 
                          color="text.secondary" 
                          gutterBottom
                          sx={{ fontWeight: 600, mb: 2, fontSize: '1.1rem' }}
                        >
                          Goods
                        </Typography>
                        <Stack spacing={2}>
                          {safeGet(() => gstinData.apiResponse.result.hsnDetails.goods, [] as any[]).map((item: any, idx: number) => (
                            <Box 
                              key={idx} 
                              sx={{ 
                                bgcolor: 'grey.50', 
                                borderRadius: 2, 
                                p: 3,
                                border: '1px solid',
                                borderColor: 'grey.200'
                              }}
                            >
                              <Typography 
                                variant="body1" 
                                fontWeight={600} 
                                sx={{ 
                                  mb: 1,
                                  fontSize: '1.1rem'
                                }}
                              >
                                {item.hsnCode}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: '1rem' }}
                              >
                                {item.hsnDescription}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Grid>
                      <Grid item xs={12} lg={6}>
                        <Typography 
                          variant="subtitle1" 
                          color="text.secondary" 
                          gutterBottom
                          sx={{ fontWeight: 600, mb: 2, fontSize: '1.1rem' }}
                        >
                          Services
                        </Typography>
                        <Stack spacing={2}>
                          {safeGet(() => gstinData.apiResponse.result.hsnDetails.services, [] as any[]).map((item: any, idx: number) => (
                            <Box 
                              key={idx} 
                              sx={{ 
                                bgcolor: 'grey.50', 
                                borderRadius: 2, 
                                p: 3,
                                border: '1px solid',
                                borderColor: 'grey.200'
                              }}
                            >
                              <Typography 
                                variant="body1" 
                                fontWeight={600} 
                                sx={{ 
                                  mb: 1,
                                  fontSize: '1.1rem'
                                }}
                              >
                                {item.hsnCode}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ fontSize: '1rem' }}
                              >
                                {item.hsnDescription}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Company Information (CIN) Card */}
        {cinData && safeGet(() => cinData.apiResponse.result.data, null) && (
          <Card 
            variant="outlined" 
            sx={{ 
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <CardHeader 
              title="Company Information (CIN)" 
              sx={{ 
                pb: 1,
                '& .MuiCardHeader-title': {
                  fontSize: '1.25rem',
                  fontWeight: 600
                }
              }}
            />
            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6} lg={4}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1rem'
                    }}
                  >
                    CIN
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontFamily: 'Roboto Mono, monospace',
                      fontSize: '1.1rem',
                      lineHeight: 1.5
                    }}
                  >
                    {safeGet(() => cinData.apiResponse.result.data.company_master_data.cin, '-')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1rem'
                    }}
                  >
                    Company Name
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.1rem',
                      lineHeight: 1.5
                    }}
                  >
                    {safeGet(() => cinData.apiResponse.result.data.company_master_data.company_name, '-')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1rem'
                    }}
                  >
                    Email ID
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.1rem',
                      lineHeight: 1.5
                    }}
                  >
                    {safeGet(() => cinData.apiResponse.result.data.company_master_data.email_id, '-')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1rem'
                    }}
                  >
                    ROC Code
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.1rem',
                      lineHeight: 1.5
                    }}
                  >
                    {safeGet(() => cinData.apiResponse.result.data.company_master_data.roc_code, '-')}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Typography 
                    variant="subtitle2" 
                    color="text.secondary" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: '1rem'
                    }}
                  >
                    RD Region
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontSize: '1.1rem',
                      lineHeight: 1.5
                    }}
                  >
                    {safeGet(() => cinData.apiResponse.result.data.company_master_data.rd_region, '-')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Processing Status Card */}
        <Card 
          variant="outlined" 
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <CardHeader 
            title="Processing Status" 
            sx={{ 
              pb: 1,
              '& .MuiCardHeader-title': {
                fontSize: '1.25rem',
                fontWeight: 600
              }
            }}
          />
          <CardContent sx={{ pt: 0 }}>
            <Stack spacing={3}>
              {kyb.metadata.results.map((result, idx) => (
                <Box 
                  key={idx} 
                  sx={{ 
                    border: 1, 
                    borderColor: 'grey.200', 
                    borderRadius: 2, 
                    p: 3,
                    bgcolor: 'grey.50'
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography 
                      variant="h6" 
                      fontWeight={600}
                      sx={{ fontSize: '1.1rem' }}
                    >
                      {result.module || 'Data Sync'}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip 
                        size="medium" 
                        color={mapStatusToChipColor(result.apiResponse.status)} 
                        label={result.apiResponse.status || '-'} 
                        sx={{ fontSize: '0.9rem', px: 2, py: 0.5 }}
                      />
                      {result.flag?.message && (
                        <Chip 
                          size="medium" 
                          color="warning" 
                          variant="outlined" 
                          label={result.flag.message} 
                          sx={{ fontSize: '0.9rem', px: 2, py: 0.5 }}
                        />
                      )}
                    </Stack>
                  </Stack>
                  <Stack spacing={1}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: '1rem' }}
                    >
                      Processed at: {new Date(result.processedAt).toLocaleString()}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: '1rem' }}
                    >
                      Attempts: {result.attempts}
                    </Typography>
                    {result.moduleId && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: '1rem' }}
                      >
                        Module ID: {result.moduleId}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}


